/* eslint-disable import/no-duplicates */
import { Stack } from "@mui/material";
import { BarSeriesType } from "@mui/x-charts";
import { MakeOptional } from "@mui/x-charts/internals";
import { differenceInDays, format, parseISO } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";
import { useCallback, useEffect, useMemo, useState } from "react";

import { theme } from "../../../../../assets";
import { PopoverCalendar } from "../../../../../components";
import {
    TEntrie,
    useReadEntries,
} from "../../../../../hooks/react-query/use-read-entries";
import { optionsPeriods, TProject } from "../../../../../models";
import Dashboard, { formatMinutesToHours } from "./dashboard";

type TAnalizyEquipTab = {
    project: TProject;
};

type Dates = {
    startAt: string;
    endAt: string;
};

type Period = "daily" | "weekly" | "monthly" | "yearly";

const detectPeriod = (start: string, end: string): Period => {
    const startDate = parseISO(start);
    const endDate = parseISO(end);
    const diffDays = differenceInDays(endDate, startDate);

    if (diffDays === 0) return "daily";
    if (diffDays <= 6) return "weekly";
    if (diffDays <= 31) return "monthly";
    return "yearly";
};

const groupEntriesByPeriod = (
    entries: TEntrie[],
    startDateISO: string,
    endDateISO: string
) => {
    const period = detectPeriod(startDateISO, endDateISO);
    let grouped: Record<string, number> = {};

    entries.forEach((entry) => {
        const date = parseISO(entry.start_at);
        let label = "";

        switch (period) {
            case "daily":
                label = format(date, "HH:mm");
                break;
            case "weekly":
                label = format(date, "EEEE", { locale: ptBR });
                break;
            case "monthly":
                label = format(date, "dd/MM");
                break;
            case "yearly":
                label = format(date, "MM/yyyy");
                break;
            default:
                label = format(date, "MM/yyyy");
        }

        grouped[label] = (grouped[label] || 0) + entry.total_time;
    });

    if (period === "weekly") {
        const daysOfWeek = [
            "domingo",
            "segunda-feira",
            "terça-feira",
            "quarta-feira",
            "quinta-feira",
            "sexta-feira",
            "sábado",
        ];

        daysOfWeek.forEach((day) => {
            if (!(day in grouped)) {
                grouped[day] = 0;
            }
        });

        grouped = daysOfWeek.reduce((acc, day) => {
            acc[day] = grouped[day];
            return acc;
        }, {} as Record<string, number>);
    }

    return { period, grouped };
};

const defaultPeriod = optionsPeriods[4].value;
export const AnalizyEquipTab = ({ project }: TAnalizyEquipTab) => {
    const [userIds, setUserIds] = useState<string[]>([]);
    const [dates, setDates] = useState<Dates>({
        startAt: defaultPeriod.from.toISOString(),
        endAt: defaultPeriod.to.toISOString(),
    });
    const { data, refetch } = useReadEntries({
        all: true,
        projectId: project.id_e,
        startDate: dates.startAt,
        endDate: dates.endAt,
    });

    const entries = data?.entries ?? [];

    const handleFilterUsersData = () =>
        entries.filter((entrie) => {
            return userIds.some((userId) => {
                return userId === entrie?.user_id;
            });
        });

    const entriesFiltered = useMemo(() => {
        const haveUserFiltered = userIds.length > 0;
        if (haveUserFiltered) return handleFilterUsersData();
        return entries;
    }, [entries, userIds]);

    const time_requested =
        data?.time_requested && typeof data?.time_requested === "object"
            ? data.time_requested
            : {};

    const handleFilterUserById = useCallback(
        (userIds?: string[]) => {
            setUserIds(userIds ?? []);
        },
        [userIds, entries]
    );

    const handleClearFilterUserById = useCallback(() => {
        setUserIds([]);
    }, [userIds]);

    const { grouped } = groupEntriesByPeriod(
        entries,
        dates.startAt,
        dates.endAt
    );

    const barChartData = Object.keys(grouped);
    const barChartSeries: MakeOptional<BarSeriesType, "type">[] = [
        {
            data: Object.values(grouped).map((val) => val),
            color: theme.primary.main,
            label: "Total de horas",
            valueFormatter: (value) => {
                return formatMinutesToHours(Number(value));
            },
        },
    ];

    const barChartContent = {
        barChartData,
        barChartSeries,
    };

    useEffect(() => {
        if (dates) {
            refetch();
        }
    }, [dates]);

    return (
        <Stack
            sx={{
                py: "2.5rem",
                gap: "2.5rem",
            }}
        >
            <PopoverCalendar
                getPeriod={(value) => {
                    setDates({
                        startAt: value?.from?.toISOString() || "",
                        endAt: value?.to?.toISOString() || "",
                    });
                }}
                optionsPeriods={optionsPeriods}
            />
            <Dashboard
                barChartContent={barChartContent}
                entries={entriesFiltered}
                handleClearFilterUserById={handleClearFilterUserById}
                handleFilterUserById={handleFilterUserById}
                time_requested={time_requested}
                selectedUsers={userIds}
                project={project}
            />
        </Stack>
    );
};
